<script lang="ts">
    import { onMount } from "svelte";
    import type { MatrixChatConnectionLike, MatrixPeerProfileDiagnostics } from "../Connection/ChatConnection";
    import ButtonClose from "../../Components/Input/ButtonClose.svelte";
    import LL from "../../../i18n/i18n-svelte";
    import { IconLoader } from "@wa-icons";
    import { modals } from "@wa-modals";

    interface Props {
        connection: MatrixChatConnectionLike;
        /** Matrix user ID (@user:server) */
        matrixUserId: string;
        /** Optional label (e.g. WA username) for the dialog title */
        label?: string;
    }

    let { connection, matrixUserId, label = undefined }: Props = $props();

    let loading = $state(true);
    let loadError: string | undefined = $state();
    let data: MatrixPeerProfileDiagnostics | undefined = $state();
    let copiedField: string | undefined = $state();

    async function loadDiagnostics() {
        loading = true;
        loadError = undefined;
        try {
            data = await connection.getMatrixPeerProfileDiagnostics(matrixUserId);
            if (!data) {
                loadError = $LL.chat.matrixPeerProfileDebug.loadError();
            }
        } catch (e) {
            console.error(e);
            loadError = $LL.chat.matrixPeerProfileDebug.loadError();
        } finally {
            loading = false;
        }
    }

    function copyText(labelKey: string, text: string) {
        navigator.clipboard
            .writeText(text)
            .then(() => {
                copiedField = labelKey;
                setTimeout(() => {
                    copiedField = undefined;
                }, 2000);
            })
            .catch(() => undefined);
    }

    onMount(() => {
        loadDiagnostics().catch(() => undefined);
    });
</script>

<div
    class="fixed inset-0 z-2001 flex items-center justify-center p-4 pointer-events-auto bg-mdark/50 backdrop-blur-md"
    role="dialog"
    aria-modal="true"
    aria-labelledby="matrix-peer-debug-title"
>
    <button
        type="button"
        class="absolute inset-0 w-full h-full cursor-default"
        aria-label={$LL.chat.matrixSettings.close()}
        onclick={() => modals.close()}
    ></button>
    <!-- svelte-ignore a11y_no_static_element_interactions -->
    <!-- svelte-ignore a11y_click_events_have_key_events -->
    <div
        class="relative flex max-h-[90vh] w-full max-w-lg flex-col overflow-hidden rounded-md2 bg-mlight/80 text-mdark shadow-light backdrop-blur-md pointer-events-auto"
        onclick={(event) => {
            event.stopPropagation();
        }}
    >
        <div class="flex items-start justify-between gap-3 border-b border-mdark/10 p-4 sm:p-5">
            <div>
                <h2 id="matrix-peer-debug-title" class="text-lg font-semibold sm:text-xl">
                    {$LL.chat.matrixPeerProfileDebug.title()}{#if label}
                        <span class="text-mdark"> — {label}</span>{/if}
                </h2>
                <p class="mt-1 text-sm text-mdark">{$LL.chat.matrixPeerProfileDebug.subtitle()}</p>
            </div>
            <ButtonClose dataTestId="close-matrix-peer-debug" onclick={() => modals.close()} />
        </div>

        <div class="min-h-0 flex-1 overflow-y-auto px-4 py-3 sm:px-5 sm:py-4">
            {#if loading}
                <div class="flex flex-col items-center justify-center gap-3 py-12 text-mdark">
                    <IconLoader class="animate-spin" font-size="2rem" />
                    <span class="text-sm">{$LL.chat.loader()}</span>
                </div>
            {:else if loadError}
                <p class="rounded-lg border border-mdanger/40 bg-mdanger/10 px-3 py-2 text-sm text-mdanger">
                    {loadError}
                </p>
            {:else if data}
                {@const snapshot = data}
                <div class="flex flex-col gap-4 text-sm">
                    <section class="flex flex-col gap-2">
                        <h3 class="text-xs font-semibold uppercase tracking-wide text-mdark">
                            {$LL.chat.matrixSettings.identitySection()}
                        </h3>
                        <button
                            type="button"
                            class="group flex w-full items-center justify-between gap-2 rounded-lg border border-mdark/10 bg-mdark/5 px-3 py-2 text-left transition hover:border-mdark/25"
                            onclick={() => copyText("id", snapshot.matrixUserId)}
                        >
                            <span class="text-mdark">{$LL.chat.matrixSettings.matrixUserId()}</span>
                            <span class="line-clamp-2 break-all text-right text-xs text-mdark"
                                >{snapshot.matrixUserId}</span
                            >
                            <span class="shrink-0 text-xs text-mblue opacity-80 group-hover:opacity-100">
                                {copiedField === "id"
                                    ? $LL.chat.matrixSettings.copied()
                                    : $LL.chat.matrixSettings.copy()}
                            </span>
                        </button>
                        <button
                            type="button"
                            class="group flex w-full items-center justify-between gap-2 rounded-lg border border-mdark/10 bg-mdark/5 px-3 py-2 text-left transition hover:border-mdark/25"
                            onclick={() => copyText("hs", snapshot.homeserverUrl)}
                        >
                            <span class="text-mdark">{$LL.chat.matrixSettings.homeserver()}</span>
                            <span class="line-clamp-2 break-all text-right text-xs text-mdark"
                                >{snapshot.homeserverUrl}</span
                            >
                            <span class="shrink-0 text-xs text-mblue opacity-80 group-hover:opacity-100">
                                {copiedField === "hs"
                                    ? $LL.chat.matrixSettings.copied()
                                    : $LL.chat.matrixSettings.copy()}
                            </span>
                        </button>
                    </section>

                    <section class="flex flex-col gap-2">
                        <h3 class="text-xs font-semibold uppercase tracking-wide text-mdark">
                            {$LL.chat.matrixSettings.profileSection()}
                        </h3>
                        <div class="rounded-lg border border-mdark/10 bg-mdark/5 px-3 py-2">
                            <div class="flex justify-between gap-2">
                                <span class="text-mdark">{$LL.chat.matrixSettings.displayName()}</span>
                                <span class="text-right text-mdark">{snapshot.profileDisplayName ?? "—"}</span>
                            </div>
                            <div class="mt-2 flex items-start justify-between gap-2">
                                <span class="shrink-0 text-mdark">{$LL.chat.matrixSettings.avatar()}</span>
                                <div class="flex min-w-0 flex-1 flex-col items-end gap-1">
                                    {#if snapshot.profileAvatarPreviewUrl}
                                        <img
                                            src={snapshot.profileAvatarPreviewUrl}
                                            alt=""
                                            class="h-12 w-12 rounded-lg object-cover"
                                        />
                                    {/if}
                                    <span class="break-all text-right text-xs text-mdark"
                                        >{snapshot.profileAvatarMxc ?? "—"}</span
                                    >
                                </div>
                            </div>
                        </div>
                    </section>
                </div>
            {/if}
        </div>
    </div>
</div>
